import { useState, useEffect } from 'react';
import { theme, buttonStyles, spacing, typography, borderRadius, mergeStyles } from '../styles/theme';

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);

  // Check if user has already accepted cookies
  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true); // Show banner if no consent stored
    }
  }, []);

  // Handle accept button click
  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted'); // Store consent
    localStorage.setItem('cookieConsentDate', new Date().toISOString()); // Store date for records
    setShowBanner(false); // Hide banner
  };

  // Don't render if banner shouldn't show
  if (!showBanner) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: theme.bg.dark,
      color: theme.text.inverse,
      padding: spacing.lg,
      boxShadow: theme.shadow.large,
      zIndex: 9999, // Above everything else
      borderTop: `3px solid ${theme.brand.primary}`,
      // Mobile adjustments
      ...(window.innerWidth <= 768 && {
        padding: spacing.md,
      })
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing.lg,
        // Stack on mobile
        ...(window.innerWidth <= 768 && {
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: spacing.md,
        })
      }}>
        {/* Cookie message */}
        <div style={{
          flex: 1,
          fontSize: typography.fontSize.sm,
          lineHeight: typography.lineHeight.normal,
          // Smaller text on mobile
          ...(window.innerWidth <= 768 && {
            fontSize: typography.fontSize.xs,
          })
        }}>
          <p style={{ margin: 0, marginBottom: spacing.xs }}>
            We use cookies to improve your experience and analyze site usage.
          </p>
          <p style={{ 
            margin: 0, 
            fontSize: typography.fontSize.xs,
            color: theme.text.muted,
          }}>
            "We use cookies to enhance your experience, remember your preferences, and understand how Judgy is used.
            This helps us improve the site and provide better features. By continuing, you accept our use of cookies." {' '}
            <a 
              href="/privacy" 
              style={{ 
                color: theme.brand.primary, 
                textDecoration: 'underline' 
              }}
            >
              Learn more in our Privacy Policy
            </a>
          </p>
        </div>

        {/* Action buttons */}
        <div style={{
          display: 'flex',
          gap: spacing.md,
          // Full width on mobile
          ...(window.innerWidth <= 768 && {
            width: '100%',
            flexDirection: 'column',
          })
        }}>
          <button
            onClick={handleAccept}
            style={mergeStyles(
              buttonStyles.base,
              buttonStyles.primary,
              {
                whiteSpace: 'nowrap', // Prevent text wrapping
                minWidth: '120px',
              },
              // Full width on mobile
              window.innerWidth <= 768 && {
                width: '100%',
              }
            )}
          >
            Accept All Cookies
          </button>
          
          {/* Optional: "Essential Only" button for stricter compliance */}
          <button
            onClick={() => {
              localStorage.setItem('cookieConsent', 'essential-only');
              localStorage.setItem('cookieConsentDate', new Date().toISOString());
              setShowBanner(false);
              // Note: You'd need to disable GA if this is clicked
              alert('Essential cookies only. Analytics disabled.');
            }}
            style={mergeStyles(
              buttonStyles.base,
              buttonStyles.secondary,
              {
                whiteSpace: 'nowrap',
                minWidth: '120px',
                backgroundColor: 'transparent',
                color: theme.text.inverse,
                border: `2px solid ${theme.border.light}`,
              },
              // Full width on mobile
              window.innerWidth <= 768 && {
                width: '100%',
              }
            )}
          >
            Essential Only
          </button>

          {/* Reject all button */}
          <button
            onClick={() => {
              localStorage.setItem('cookieConsent', 'rejected');
              localStorage.setItem('cookieConsentDate', new Date().toISOString());
              setShowBanner(false);
              window['ga-disable-' + process.env.REACT_APP_GA_MEASUREMENT_ID] = true; // Disable GA
            }}
            style={mergeStyles(
              buttonStyles.base,
              buttonStyles.secondary,
              {
                whiteSpace: 'nowrap',
                mionWidth: '120px',
                backgroundColor: 'transparent',
                color: theme.text.muted,
                border: `2px solid ${theme.border.medium}`,
              },
              window.innerWidth <= 768 && {
                width: '100%',
              }
            )}
          >
            Reject All
          </button>
        </div>
      </div>
    </div>
  );
}